import React from 'react';
import { useToast, Box, Text, VStack } from '@chakra-ui/react';

interface CacheRefreshButtonProps {
  currentTab?: 'dashboard' | 'faq';
}

const CacheRefreshButton: React.FC<CacheRefreshButtonProps> = ({ currentTab = 'dashboard' }) => {
  const toast = useToast();

  const handleCacheRefresh = async () => {
    try {
      // Store current tab state FIRST before any cache operations
      // Use multiple storage methods for reliability
      localStorage.setItem('tempActiveTab', currentTab);
      sessionStorage.setItem('tempActiveTab', currentTab);
      if (currentTab === 'faq') {
        window.location.hash = '#faq';
      }
      console.log('Storing tab state:', currentTab); // Debug log

      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Clear localStorage (optional - preserves user data)
      // localStorage.clear();

      // Unregister service worker and re-register
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.unregister()));
        
        // Re-register after a short delay
        setTimeout(() => {
          navigator.serviceWorker.register('/sw.js');
        }, 100);
      }

      toast({
        position: 'top',
        render: () => (
          <Box
            bg="rgba(8,11,20,0.92)"
            color="white"
            px={5} py={4}
            borderRadius="18px"
            display="inline-block"
            border="1px solid rgba(255,255,255,0.12)"
            boxShadow="0 12px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)"
            backdropFilter="blur(28px)"
            sx={{ WebkitBackdropFilter: 'blur(28px)' }}
          >
            <VStack align="start" spacing={0.5}>
              <Text fontWeight="700" fontSize="sm" whiteSpace="nowrap">Cache Cleared!</Text>
              <Text fontSize="xs" color="rgba(255,255,255,0.45)" whiteSpace="nowrap">App cache refreshed. Reloading...</Text>
            </VStack>
          </Box>
        ),
        duration: 2000,
      });

      // Force reload
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      toast({
        position: 'top',
        render: () => (
          <Box
            bg="rgba(8,11,20,0.92)"
            color="white"
            px={5} py={4}
            borderRadius="18px"
            display="inline-block"
            border="1px solid rgba(248,113,113,0.4)"
            boxShadow="0 12px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)"
            backdropFilter="blur(28px)"
            sx={{ WebkitBackdropFilter: 'blur(28px)' }}
          >
            <VStack align="start" spacing={0.5}>
              <Text fontWeight="700" fontSize="sm" whiteSpace="nowrap">Refresh Failed</Text>
              <Text fontSize="xs" color="rgba(255,255,255,0.45)" whiteSpace="nowrap">Unable to clear cache. Please try again.</Text>
            </VStack>
          </Box>
        ),
        duration: 3000,
      });
    }
  };

  return (
    <Box
      as="button"
      onClick={handleCacheRefresh}
      px={3} py={1.5}
      borderRadius="8px"
      border="1px solid rgba(255,255,255,0.18)"
      bg="transparent"
      cursor="pointer"
      transition="all 0.15s"
      _hover={{ borderColor: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.04)' }}
      _focus={{ outline: 'none', boxShadow: 'none' }}
      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
    >
      <Text fontSize="11px" fontWeight="700" color="rgba(255,255,255,0.75)" whiteSpace="nowrap">
        Refresh App
      </Text>
    </Box>
  );
};

export default CacheRefreshButton; 